import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { SignInButton, SignUpButton } from "@clerk/react";
import { X, Sparkles, LogIn, Lock } from "lucide-react";
import { PupLogo } from "./pup-logo";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** When set, shows the user's first frame (blurred) as a locked teaser. */
  previewImageUrl?: string | null;
}

export function AuthModal({ isOpen, onClose, title, previewImageUrl }: AuthModalProps) {
  const redirectUrl =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/";

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Gated-generation scenario: keep the focus on the user's own (blurred)
  // generation with a single Sign Up CTA. No perks, no big secondary button —
  // just the teaser and the one action that unlocks it.
  if (previewImageUrl) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/65 backdrop-blur-md"
          onClick={onClose}
        />
        <div
          className="relative w-full max-w-[400px] rounded-3xl overflow-hidden border border-white/10 shadow-[0_32px_100px_rgba(0,0,0,0.5)] animate-fade-in z-10"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-1.5 rounded-xl bg-black/30 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/50 transition-all duration-200 border border-white/10"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>

          <div className="relative h-[min(560px,82vh)] w-full">
            <img
              src={previewImageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "blur(15px)", transform: "scale(1.25)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/35 to-black/90" />

            <div className="relative h-full flex flex-col items-center justify-end text-center px-7 pb-8">
              <div className="size-14 rounded-full bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center mb-4">
                <Lock className="size-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight drop-shadow-lg leading-tight">
                {title || "Sign Up to View Generation"}
              </h2>
              <p className="text-[13px] text-white/80 mt-2 mb-6 drop-shadow max-w-[280px]">
                Sign up to view &amp; download your generation in full quality.
              </p>

              <SignUpButton
                mode="modal"
                forceRedirectUrl={redirectUrl}
                signInForceRedirectUrl={redirectUrl}
              >
                <button
                  onClick={onClose}
                  className="btn-primary w-full py-3.5 text-[15px] flex items-center justify-center gap-2 rounded-xl"
                >
                  <Sparkles className="size-4" />
                  Sign Up Free
                </button>
              </SignUpButton>

              <SignInButton
                mode="modal"
                forceRedirectUrl={redirectUrl}
                signUpForceRedirectUrl={redirectUrl}
              >
                <button
                  onClick={onClose}
                  className="mt-3.5 text-[12.5px] text-white/65 hover:text-white font-medium transition-colors duration-200"
                >
                  Already have an account?{" "}
                  <span className="underline underline-offset-2">Log in</span>
                </button>
              </SignInButton>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const preview = previewImageUrl ? (
    <div
      className="relative rounded-2xl overflow-hidden mb-6"
      style={{ aspectRatio: "16/10" }}
    >
      <img
        src={previewImageUrl}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "blur(16px)", transform: "scale(1.2)" }}
      />
      <div className="absolute inset-0 bg-black/45 flex flex-col items-center justify-center gap-2">
        <div className="size-12 rounded-full bg-white/15 border border-white/25 backdrop-blur flex items-center justify-center">
          <Lock className="size-5 text-white" />
        </div>
        <p className="text-white text-[13px] font-semibold drop-shadow">
          Your video is ready
        </p>
      </div>
    </div>
  ) : null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Desktop: right-side panel */}
      <div
        className="hidden md:flex fixed right-0 top-0 bottom-0 w-[400px] border-l border-white/8 shadow-[-20px_0_60px_rgba(0,0,0,0.3)] flex-col animate-slide-in-right z-10"
        style={{
          background: "linear-gradient(180deg, #2a2035 0%, #1a1520 100%)",
        }}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/6">
          <div className="flex items-center gap-2">
            <PupLogo className="size-7" />
            <span className="text-[14px] font-bold text-foreground">
              AnimePup
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center px-8">
          {preview}
          <h2 className="text-2xl font-bold text-foreground tracking-tight mb-2">
            {title || "Get started"}
          </h2>
          <p className="text-[14px] text-muted-foreground mb-8 leading-relaxed">
            Create anime characters, edit their look, and bring them to life
            with AI animation.
          </p>

          <div className="space-y-3">
            <SignUpButton
              mode="modal"
              forceRedirectUrl={redirectUrl}
              signInForceRedirectUrl={redirectUrl}
            >
              <button
                onClick={onClose}
                className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl"
              >
                <Sparkles className="size-4" />
                Sign Up Free
              </button>
            </SignUpButton>

            <SignInButton
              mode="modal"
              forceRedirectUrl={redirectUrl}
              signUpForceRedirectUrl={redirectUrl}
            >
              <button
                onClick={onClose}
                className="btn-glass w-full py-2.5 text-[14px] flex items-center justify-center gap-2 rounded-xl"
              >
                <LogIn className="size-4" />
                Log In
              </button>
            </SignInButton>
          </div>

          <div className="mt-6 p-3.5 rounded-xl card-glass border border-[#d4a574]/15">
            <p className="text-[12px] text-muted-foreground leading-relaxed">
              <strong className="text-foreground">84 free gizmos</strong> on
              signup — enough to create several characters and animations. No
              credit card required.
            </p>
          </div>
        </div>

        <div className="px-8 pb-6 text-[11px] text-muted-foreground">
          By signing up you agree to our{" "}
          <Link
            to="/terms"
            onClick={onClose}
            className="text-[#d4a574] hover:underline"
          >
            Terms of Service
          </Link>{" "}
          and content policy.
        </div>
      </div>

      {/* Mobile: full-screen takeover */}
      <div
        className="md:hidden fixed inset-0 flex flex-col animate-fade-in z-10"
        style={{
          background: "linear-gradient(180deg, #2a2035 0%, #1a1520 100%)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-white/6">
          <div className="flex items-center gap-2">
            <PupLogo className="size-7" />
            <span className="text-[14px] font-bold text-foreground">
              AnimePup
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-7 pt-10 pb-6 flex flex-col">
          {preview}
          <div className="flex flex-col items-center text-center mb-9 animate-fade-in-up">
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-full bg-[#d4a574]/15 blur-[40px] scale-[3] pointer-events-none" />
              <PupLogo className="size-16 relative" />
            </div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight leading-tight mb-2">
              {title || "Get started"}
            </h2>
            <p className="text-[14px] text-muted-foreground leading-relaxed max-w-[280px]">
              Create anime characters, edit their look, and bring them to life
              with AI animation.
            </p>
          </div>

          <div className="space-y-3 animate-fade-in-up delay-100">
            <SignUpButton
              mode="modal"
              forceRedirectUrl={redirectUrl}
              signInForceRedirectUrl={redirectUrl}
            >
              <button
                onClick={onClose}
                className="btn-primary w-full py-3.5 text-[15px] flex items-center justify-center gap-2 rounded-xl"
              >
                <Sparkles className="size-4" />
                Sign Up Free
              </button>
            </SignUpButton>

            <SignInButton
              mode="modal"
              forceRedirectUrl={redirectUrl}
              signUpForceRedirectUrl={redirectUrl}
            >
              <button
                onClick={onClose}
                className="btn-glass w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl"
              >
                <LogIn className="size-4" />
                Log In
              </button>
            </SignInButton>
          </div>

          <div className="mt-6 p-4 rounded-xl card-glass border border-[#d4a574]/15 animate-fade-in-up delay-200">
            <p className="text-[12px] text-muted-foreground leading-relaxed text-center">
              <strong className="text-foreground">84 free gizmos</strong> on
              signup — enough to create several characters and animations. No
              credit card required.
            </p>
          </div>
        </div>

        <div className="shrink-0 px-7 pb-6 pt-3 text-[11px] text-muted-foreground text-center">
          By signing up you agree to our{" "}
          <Link
            to="/terms"
            onClick={onClose}
            className="text-[#d4a574] hover:underline"
          >
            Terms of Service
          </Link>{" "}
          and content policy.
        </div>
      </div>
    </div>,
    document.body,
  );
}
